import React, { Fragment, useEffect, useState } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faBook, faImages, faUser as farUser } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import Dialogs from '../Common/Dialog';
import StepsBar from '../StepsBar/StepsBar';
import {
  openPlanPurchaseModal,
  setBookData,
  setBookId,
  setCurrentStep,
  setDraftId,
  setStartNewStory,
} from '../../store/appSlice';
import { setStoryData } from '../../store/storySlice';
import { setShowLogin } from '../../store/userSlice';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import '../../App.css';
import { saveDraft } from './services/navbar.service';
import { UserLocalStorageKeysEnum, UserRoleEnum } from '../../interfaces/user.interface';
import { logOut } from '../../store/thunks/user.thunk';
import { checkSubscribedToPlan } from '../../pages/Builder/services/builderHelpers';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface NavigationItem {
  name: string;
  href?: string;
  current: boolean;
  onClick?: () => void;
  toHide?: (to: string) => boolean;
}

interface NavigationBarProps {
  toggleSignPage?: (isLogin: boolean) => void;
  isLogin?: boolean;
  hideActions?: boolean;
}

const NavigationBar = ({ toggleSignPage, isLogin, hideActions = false }: NavigationBarProps) => {
  const showLogin = useSelector((state: RootState) => state.user.showLogin);
  const user = useSelector((state: RootState) => state.user.user);
  const singleBookPlan = useSelector((state: RootState) => state.user.singleBookPlan);
  const bookData = useSelector((state: RootState) => state.app.bookData);
  const currentStep = useSelector((state: RootState) => state.app.currentStep);
  const currentPathname = useSelector((state: RootState) => state.app.currentPathname);
  const isOnboarding = useSelector((state: RootState) => state.user.isOnboarding);
  const navigateTo = useNavigate();
  const [bookId, setActiveBookId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isOnboardingFlow, setIsOnboardingFlow] = useState(false);
  const [libraryPath, setLibraryPath] = useState<string>('/library');
  const [storiesRemaining, setStoriesRemaining] = useState(0);
  const dispatch: AppDispatch = useDispatch();
  const [planSubscribed, setPlanSubscribed] = useState<boolean>(false);
  const [checkedUserState, setCheckedUserState] = useState<boolean>(false);
  const [loaders, setLoaders] = useState({
    characterGenerating: false,
    characterRegenerate: false,
    planExist: false,
  });

  const [navigation, setNavigation] = useState<NavigationItem[]>([]);

  const logo = '/logo.webp';

  const handleLogout = async () => dispatch(logOut({ user, navigateTo }));

  useEffect(() => {
    if (currentPathname?.includes('/teacher/onboarding')) setIsOnboardingFlow(true);
    else setIsOnboardingFlow(false);
  }, [currentPathname]);

  useEffect(() => {
    const idBook = localStorage.getItem(UserLocalStorageKeysEnum.BOOK_ID);

    if (idBook && user && user?.email) setActiveBookId(idBook);
  }, [currentStep, localStorage.getItem(UserLocalStorageKeysEnum.BOOK_ID)]);

  useEffect(() => {
    if (localStorage.getItem(UserLocalStorageKeysEnum.EDIT_MODE)) setEditMode(true);
    else setEditMode(false);
  }, [localStorage.getItem(UserLocalStorageKeysEnum.EDIT_MODE)]);

  useEffect(() => {
    if (user) {
      setStoriesRemaining(+(user?.booksCreateLimit || 0) - +(user?.booksCreatedCounter || 0));
    }
  }, [user]);

  useEffect(() => {
    if (user && !checkedUserState) {
      setCheckedUserState(true);
      checkSubscribedToPlan(user, setLoaders, setPlanSubscribed, loaders, navigateTo, dispatch);
    }
  }, [user, checkedUserState]);

  useEffect(() => {
    const libraryUrlPath = user?.isTeacherMode ? '/classroom/library' : '/library';
    setLibraryPath(libraryUrlPath);
    if (user && (user?.plan || user?.isTrialFreeAccess === true) && user?.verified) {
      let storiesRemaining = +(user?.booksCreateLimit || 0) - +(user?.booksCreatedCounter || 0);
      if (singleBookPlan) {
        setNavigation([
          {
            name: 'Create New Story',
            href: '/',
            current: false,
            toHide: () => currentStep === 0 && currentPathname === '/',
          },
          {
            name: 'My Library',
            href: libraryUrlPath,
            current: false,
            toHide: () => currentPathname?.includes('/classroom'),
          },
          { name: `Stories`, href: '#', current: false },
        ]);
      } else {
        setNavigation([
          {
            name: 'Create New Story',
            href: '/',
            current: false,
            toHide: () => currentStep === 0 && currentPathname === '/',
          },
          {
            name: 'My Library',
            href: libraryUrlPath,
            current: false,
            toHide: () => currentPathname?.includes('/classroom'),
          },
          { name: `Stories`, href: '#', current: false },
        ]);
      }
    } else {
      if (user && !user?.plan && user?.verified) {
        setNavigation([
          { name: 'Purchase Plan', current: false, onClick: () => openPurchasePlanPopup() },
          {
            name: 'My Library',
            href: libraryUrlPath,
            current: false,
            toHide: () => currentPathname?.includes('/classroom'),
          },
        ]);
      } else {
        setNavigation([]);
      }
    }
  }, [user, currentPathname, currentStep, singleBookPlan]);

  const isOnboardingProcess = () => {
    return isOnboarding || currentPathname?.includes('/teacher/onboarding');
  };

  const handleSaveDraft = async () => {
    await saveDraft(user?.email!, bookId!, currentStep, dispatch, setLoader, setOpenDialog);
  };

  const handleStartNewStory = () => {
    dispatch(setCurrentStep(1));
    dispatch(setDraftId(null));
    localStorage.removeItem(UserLocalStorageKeysEnum.BOOK_ID);
    localStorage.removeItem(UserLocalStorageKeysEnum.EDIT_MODE);
    localStorage.removeItem(UserLocalStorageKeysEnum.STORY_STEP);
    dispatch(
      setStoryData({
        characterBuilding: false,
        showLibrary: false,
        storybuildingError: false,
        isDummyPic: false,
        creditsPerBookLimit: 0,
      }),
    );
    dispatch(setBookData(null));
    dispatch(setBookId(null));
    navigateTo('/');
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    if (currentStep > 1) dispatch(setStartNewStory(false));
  }, [currentStep]);

  // Handle navigation state when user is not logged in or not verified
  useEffect(() => {
    if (!user || !user.verified) {
      setNavigation([]);
    }
  }, [user]);

  const NavigationItem = (item: any) => {
    return (
      <React.Fragment key={item.name}>
        {item.name === 'Stories' ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon
              icon={faBook}
              className="cursor-pointer"
              title="Remaining Stories"
              style={{
                color: '#042c6e',
                marginTop: '0',
                marginLeft: '6px',
                fontSize: '24px',
                marginBottom: '0',
              }}
            />
            <span className="text-sm pl-1 text-storyblue">{storiesRemaining}</span>
          </div>
        ) : (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => item.onClick && item.onClick()}
            className={classNames(
              item.current
                ? 'bg-transparent text-storyblue underline'
                : 'text-white  bg-storyblueSecondLightText border border-storyblueSecondLightText rounded-2xl hover:shadow-md', // Add hover effect for border
              'px-4 py-2 text-sm font-bold flex gap-2 items-center h-9',
              currentPathname && !!item.toHide && item.toHide(item.href) ? 'hidden' : 'flex',
              isOnboardingProcess() ? 'pointer-events-none opacity-50' : '',
            )}
            aria-current={item.current ? 'page' : undefined}
          >
            {item.name}
          </Link>
        )}
      </React.Fragment>
    );
  };

  const NavigateLink = (
    title: string,
    to: string | null,
    onclick?: () => void,
    toHide?: (to: string | null) => boolean,
  ) => {
    return (
      <Link
        className={classNames(
          (false as any).current
            ? 'bg-transparent text-storyblue underline'
            : 'text-white  bg-storyblueSecondLightText border border-storyblueSecondLightText rounded-2xl hover:shadow', // Add hover effect for border
          'px-4 py-2 sm:py-0 md:py-0 sm:text-xs lg:text-sm font-bold flex gap-2 items-center h-9',
          !!toHide && toHide(to) ? 'hidden' : '',
          isOnboardingProcess() ? 'pointer-events-none opacity-50' : '',
        )}
        to={to as any}
        onClick={() => onclick && onclick()}
      >
        {title}
      </Link>
    );
  };

  const openPurchasePlanPopup = () => {
    setTimeout(() => {
      dispatch(
        openPlanPurchaseModal({
          show: true,
          data: {
            title: 'Purchase plan!',
            description:
              "Order a physical copy of the book you're creating, or subscribe to a limited-time free trial to finish your current story and start new ones!",
          },
        }),
      );
    }, 0);
  };

  const openLoginOrSignUpPage = (isLogin: boolean) => {
    // if (currentPathname === '/') {
    //   if (toggleSignPage) {
    //     toggleSignPage(isLogin);
    //   } else {
    //     dispatch(setShowLogin(isLogin));
    //   }
    // } else {
    //   navigateTo(isLogin ? '/signin' : '/signup');
    // }
    navigateTo('/auth');
  };

  return (
    <>
      <Disclosure as="nav" className="bg-white py-[1.3rem] p-[.3rem] md:p-6 header-bg">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-7xl sm:px-6 h-10 md:h-16">
              <div className="relative flex items-center justify-between">
                {/* Mobile menu button */}
                <div className="flex-1 flex items-center  sm:items-stretch justify-between">
                  {!isOnboardingFlow && !hideActions && (
                    <div className="sm:hidden md:absolute inset-y-0 left-0 flex items-center ">
                      <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none">
                        <span className="sr-only">Open main menu</span>
                        {open ? (
                          <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                        ) : (
                          <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                        )}
                      </Disclosure.Button>
                    </div>
                  )}
                  {/* User profile dropdown (centered on mobile) */}
                  <Link to="http://buildmystory.com/" target="_blank" className="flex-shrink-0 flex items-center">
                    <img className="h-[2.5rem] lg:h-[51px] w-[8.5rem]  lg:w-[11rem]" src={logo} alt="Build My Story" />
                  </Link>
                  {!isOnboardingFlow && !hideActions && (
                    <>
                      {user ? (
                        <div
                          className={`relative md:hidden ${isOnboardingProcess() ? 'pointer-events-none opacity-50' : ''}`}
                        >
                          <Menu as="div" className="relative inline-block text-center">
                            <Menu.Button className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50">
                              <FontAwesomeIcon icon={farUser} className="mr-2" />

                              <FontAwesomeIcon icon={faAngleDown} className="ml-2" />
                            </Menu.Button>
                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <Menu.Items className="absolute right-0 z-40  mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {user?.role === UserRoleEnum.student ? (
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        onClick={handleLogout}
                                        className={classNames(
                                          active ? 'bg-gray-100' : '',
                                          'block w-full text-left px-4 py-2 text-sm text-gray-700',
                                        )}
                                      >
                                        Sign out
                                      </button>
                                    )}
                                  </Menu.Item>
                                ) : (
                                  <>
                                    {user?.isTeacherMode && (
                                      <Menu.Item>
                                        {({ active }) => (
                                          <Link
                                            to="/classroom/dashboard"
                                            className={classNames(
                                              active ? 'bg-gray-100' : '',
                                              'block text-left px-4 py-2 text-sm text-gray-700',
                                            )}
                                          >
                                            Dashboard
                                          </Link>
                                        )}
                                      </Menu.Item>
                                    )}
                                    <Menu.Item>
                                      {({ active }) => (
                                        <Link
                                          to="/updateProfile"
                                          className={classNames(
                                            active ? 'bg-gray-100' : '',
                                            'block text-left px-4 py-2 text-sm text-gray-700',
                                          )}
                                        >
                                          My Profile
                                        </Link>
                                      )}
                                    </Menu.Item>
                                    {user.planRef && (
                                      <Menu.Item>
                                        {({ active }) => (
                                          <Link
                                            to="/myPlans"
                                            className={classNames(
                                              active ? 'bg-gray-100' : '',
                                              'block text-left px-4 py-2 text-sm text-gray-700',
                                            )}
                                          >
                                            My Plan
                                          </Link>
                                        )}
                                      </Menu.Item>
                                    )}
                                    <Menu.Item>
                                      {({ active }) => (
                                        <Link
                                          to={libraryPath}
                                          className={classNames(
                                            active ? 'bg-gray-100' : '',
                                            'block text-left px-4 py-2 text-sm text-gray-700',
                                          )}
                                        >
                                          My Library
                                        </Link>
                                      )}
                                    </Menu.Item>
                                    {!user.isTrialFreeAccess &&
                                      ![UserRoleEnum.student, UserRoleEnum.teacher].includes(user?.role!) && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <Link
                                              to="/OrderHistory"
                                              className={classNames(
                                                active ? 'bg-gray-100' : '',
                                                'block text-left px-4 py-2 text-sm text-gray-700',
                                              )}
                                            >
                                              My Orders
                                            </Link>
                                          )}
                                        </Menu.Item>
                                      )}
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={handleLogout}
                                          className={classNames(
                                            active ? 'bg-gray-100' : '',
                                            'block w-full text-left px-4 py-2 text-sm text-gray-700',
                                          )}
                                        >
                                          Sign out
                                        </button>
                                      )}
                                    </Menu.Item>
                                  </>
                                )}
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        </div>
                      ) : isLogin || (showLogin && isLogin === undefined) ? (
                        <button
                          onClick={() => {
                            dispatch(setShowLogin(false));
                          }}
                          className="bg-transparent md:hidden text-storyblue rounded-2xl px-4 py-2 text-sm font-medium hover:bg-gray-700 hover:text-white"
                        >
                          Sign Up
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              dispatch(setShowLogin(true));
                            }}
                            className="bg-transparent md:hidden text-storyblue rounded-2xl px-4 py-2 text-sm font-medium hover:bg-gray-700 hover:text-white"
                          >
                            Log In
                          </button>
                          {}
                        </>
                      )}{' '}
                    </>
                  )}

                  {!isOnboardingFlow && !hideActions && (
                    <div className="hidden sm:flex width:[50%] justify-between  h-16 gap-4 items-center">
                      <div className="ml-2 flex items-center h-full">
                        <div className="space-x-4 flex gap-2 items-center">
                          {user?.email && user.verified && !bookId ? (
                            navigation.map((item) => NavigationItem(item))
                          ) : (user?.plan || user?.isTrialFreeAccess === true) &&
                            user?.email &&
                            user?.verified &&
                            bookId ? (
                            <div className="flex gap-2 items-center">
                              {currentStep === 6
                                ? NavigateLink(
                                    'Create New Story',
                                    '/',
                                    handleStartNewStory,
                                    () => +currentStep === 0 && currentPathname === '/',
                                  )
                                : bookId && window.location.hash.toString() !== '#/'
                                  ? NavigateLink('Go To Story', '/')
                                  : NavigateLink('Create New Story', null, () => setOpenDialog(true))}
                              {libraryPath &&
                                NavigateLink(
                                  'My Library',
                                  libraryPath,
                                  () => currentStep === 6 && handleStartNewStory(),
                                  () => currentPathname?.includes('/classroom'),
                                )}
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <FontAwesomeIcon
                                  icon={faBook}
                                  className="cursor-pointer"
                                  title="Remaining Stories"
                                  style={{
                                    color: '#042c6e',
                                    marginTop: '0',
                                    marginLeft: '6px',
                                    fontSize: '24px',
                                    marginBottom: '0',
                                  }}
                                />
                                <span className="text-sm pl-1 text-storyblue">{storiesRemaining}</span>
                              </div>
                              {/* {parseInt(bookData?.creditsPerBookLimit) >= 0 && (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <FontAwesomeIcon
                                                                    icon={faImages}
                                                                    className="cursor-pointer"
                                                                    title="Remaining Illustrations"
                                                                    style={{
                                                                        color: '#042c6e',
                                                                        marginTop: '12px',
                                                                        marginLeft: '6px',
                                                                        fontSize: '24px',
                                                                        marginBottom: '6px'
                                                                    }}
                                                                />
                                                                <span className="text-sm pl-1 mt-2 text-storyblue">
                                                                    {bookData?.creditsPerBookLimit}
                                                                </span>
                                                            </div>
                                                        )} */}
                            </div>
                          ) : !user?.plan && user?.isTrialFreeAccess !== true && user?.email && user?.verified ? (
                            <>
                              {NavigateLink('Purchase Plan', null, () => openPurchasePlanPopup())}
                              {NavigateLink('My Library', libraryPath)}
                            </>
                          ) : (
                            ''
                          )}
                        </div>
                      </div>
                      <div
                        className={`flex items-center h-full ${isOnboardingProcess() ? 'pointer-events-none opacity-50' : ''}`}
                      >
                        {user ? (
                          <>
                            {/* Profile dropdown */}
                            <Menu as="div" className="relative">
                              <div>
                                <Menu.Button className="flex rounded-full text-sm focus:outline-none items-center">
                                  <span className="sr-only">Open user menu</span>
                                  <FontAwesomeIcon
                                    icon={farUser}
                                    className="cursor-pointer"
                                    title="User Menu"
                                    style={{
                                      marginBottom: '6px',
                                      marginLeft: '6px',
                                      fontSize: '24px',
                                      color: '#042c6e',
                                    }}
                                  />
                                  <FontAwesomeIcon
                                    icon={faAngleDown}
                                    className="cursor-pointer"
                                    title="User Menu"
                                    style={{ color: '#042c6e', marginLeft: '6px' }}
                                  />
                                </Menu.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className="absolute right-0 z-40  mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {user?.role === UserRoleEnum.student ? (
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={handleLogout}
                                          className={classNames(
                                            active ? 'bg-gray-100' : '',
                                            'block w-full text-left px-4 py-2 text-sm text-gray-700',
                                          )}
                                        >
                                          Sign out
                                        </button>
                                      )}
                                    </Menu.Item>
                                  ) : (
                                    <>
                                      {user?.isTeacherMode && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <Link
                                              to="/classroom/dashboard"
                                              className={classNames(
                                                active ? 'bg-gray-100' : '',
                                                'block text-left px-4 py-2 text-sm text-gray-700',
                                              )}
                                            >
                                              Dashboard
                                            </Link>
                                          )}
                                        </Menu.Item>
                                      )}
                                      <Menu.Item>
                                        {({ active }) => (
                                          <Link
                                            to="/updateProfile"
                                            className={classNames(
                                              active ? 'bg-gray-100' : '',
                                              'block text-left px-4 py-2 text-sm text-gray-700',
                                            )}
                                          >
                                            My Profile
                                          </Link>
                                        )}
                                      </Menu.Item>
                                      {user.planRef && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <Link
                                              to="/myPlans"
                                              className={classNames(
                                                active ? 'bg-gray-100' : '',
                                                'block text-left px-4 py-2 text-sm text-gray-700',
                                              )}
                                            >
                                              My Plan
                                            </Link>
                                          )}
                                        </Menu.Item>
                                      )}
                                      <Menu.Item>
                                        {({ active }) => (
                                          <Link
                                            to={libraryPath}
                                            className={classNames(
                                              active ? 'bg-gray-100' : '',
                                              'block text-left px-4 py-2 text-sm text-gray-700',
                                            )}
                                          >
                                            My Library
                                          </Link>
                                        )}
                                      </Menu.Item>
                                      {!user.isTrialFreeAccess &&
                                        ![UserRoleEnum.student, UserRoleEnum.teacher].includes(user?.role!) && (
                                          <Menu.Item>
                                            {({ active }) => (
                                              <Link
                                                to="/OrderHistory"
                                                className={classNames(
                                                  active ? 'bg-gray-100' : '',
                                                  'block text-left px-4 py-2 text-sm text-gray-700',
                                                )}
                                              >
                                                My Orders
                                              </Link>
                                            )}
                                          </Menu.Item>
                                        )}
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={handleLogout}
                                            className={classNames(
                                              active ? 'bg-gray-100' : '',
                                              'block w-full text-left px-4 py-2 text-sm text-gray-700',
                                            )}
                                          >
                                            Sign out
                                          </button>
                                        )}
                                      </Menu.Item>
                                    </>
                                  )}
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          </>
                        ) : isLogin || (showLogin && isLogin === undefined) ? (
                          <button
                            onClick={() => {
                              openLoginOrSignUpPage(false);
                            }}
                            className="bg-transparent text-storyblue rounded-2xl px-4 py-2 text-sm font-medium hover:bg-gray-700 hover:text-white"
                          >
                            Sign Up
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                openLoginOrSignUpPage(true);
                              }}
                              className="bg-transparent text-storyblue rounded-2xl px-4 py-2 text-sm font-medium hover:bg-gray-700 hover:text-white"
                            >
                              Log In
                            </button>
                            {}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile menu */}
            {!hideActions && <Disclosure.Panel className="sm:hidden flex flex-col  items-start">
              <div className=" pt-2 pb-3 space-y-1 ">
                {user?.email && user.verified && !bookId ? (
                  navigation.map((item) => NavigationItem(item))
                ) : user?.plan && user?.email && user?.verified && bookId ? (
                  <div className="flex flex-col    gap-2">
                    {currentStep === 6
                      ? NavigateLink('Create New Story', '/', handleStartNewStory)
                      : bookId && window.location.hash.toString() !== '#/'
                        ? NavigateLink('Go To Story', '/')
                        : currentStep !== 0
                          ? NavigateLink('Create New Story', null, () => setOpenDialog(true))
                          : NavigateLink('Create New Story', '/', handleStartNewStory)}
                    {NavigateLink('My Library', libraryPath)}
                    {/*{!singleBookPlan && NavigateLink('Buy Illustration & Story Credits', '/ImageTokens')}*/}
                    {/* {NavigateLink(`Stories: ${storiesRemaining}`, "#")} */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FontAwesomeIcon
                        icon={faBook}
                        className="cursor-pointer"
                        title="Remaining Stories"
                        style={{
                          color: '#042c6e',
                          marginTop: '12px',
                          marginLeft: '6px',
                          fontSize: '24px',
                          marginBottom: '6px',
                        }}
                      />
                      <span className="text-sm pl-1 mt-2 text-storyblue">{storiesRemaining}</span>
                    </div>
                    {+(bookData?.creditsPerBookLimit || 0) >= 0 && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon
                          icon={faImages}
                          className="cursor-pointer"
                          title="Remaining Stories"
                          style={{
                            color: '#042c6e',
                            marginTop: '12px',
                            marginLeft: '6px',
                            fontSize: '24px',
                            marginBottom: '6px',
                          }}
                        />
                        <span className="text-sm pl-1 mt-2 text-storyblue">{bookData?.creditsPerBookLimit}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  ''
                )}
              </div>
            </Disclosure.Panel>}
          </>
        )}
      </Disclosure>
      <Dialogs
        openDialog={openDialog}
        handelClose={handleClose}
        handelProcess={handleSaveDraft}
        confirmMessage="Are you sure you want to save current Story & start new Story?"
        loader={loader}
      />
      <div className="bg-storytan">{user && user?.verified && <StepsBar currentStep={currentStep} />}</div>
    </>
  );
};

export default NavigationBar;
